import { $, $$ } from '../core/text.js';
import { mitLadezustand } from '../ui/busy.js';
import { AUTH_DRIN, merkeSitzung, setzeAuthPhase } from './phase.js';
import {
  cloudAuthConfig, cloudConfirmEmailChange, cloudForgot, cloudLogin, cloudNonce,
  cloudOAuth, cloudRegister, cloudResendVerification, cloudResetPassword,
  cloudVerifyEmail, hasSession,
} from './account.js';

/* ══════════ Anmeldebildschirm (Cloud-Modus) ══════════
   Drei Wege hinein: Google, Apple, oder E-Mail + Passwort. Dazu der Weg zurück,
   wenn das Passwort weg ist (Reset per E-Mail).

   Der frühere „Anmelde-Link per E-Mail" ist bewusst entfernt. Er war so stark
   wie das Postfach dahinter, machte den Bildschirm unklar (weder Anmelden noch
   Registrieren) und ist durch den Reset-Flow vollständig ersetzt. */

let onSignedIn = null;      // von wireAuth gesetzt: (user) => Promise
let busy = false;
let pendingResetToken = ''; // aus ?reset=… in der URL
let wired = false;

function msg(text, kind) {
  const el = $('#auth-status');
  if (!el) return;
  el.textContent = text || '';
  el.className = 'auth-status' + (kind ? ' ' + kind : '');
}

/* Doppelte Submits abfangen – sonst laufen zwei Anmeldungen parallel und die
   zweite scheitert am Rate-Limit.

   Der Knopf behält dabei seine Beschriftung und bekommt nur einen Kreisel
   davor (ui/busy.js). Vorher wurde der Text durch „Einen Moment …" ersetzt:
   deutlich breiter, also wuchs der Knopf und alles darunter rutschte – und
   wer nach dem Klick wegsah, konnte nicht mehr nachlesen, was gerade läuft. */
async function guard(btn, fn) {
  if (busy) return;
  busy = true;
  try { await mitLadezustand(btn, fn); }
  catch (err) { msg(err.message || 'Das hat nicht geklappt.', 'err'); }
  finally { busy = false; }
}

async function signedIn(user, text, frischAngelegt = false) {
  msg(text, 'ok');
  /* Erst die Phase, dann die App: das Gerüst wird sichtbar, während enterApp
     noch den Lernstand holt. Sonst stünde der Anmeldebildschirm bis zum Ende
     der Übertragung da und die App erschiene schlagartig fertig. */
  setzeAuthPhase(AUTH_DRIN);
  // Damit der nächste Start direkt mit der App-Hülle beginnt statt mit dem
  // Anmeldebildschirm. Nur ein Merker fürs Laden, kein Anmeldezustand.
  merkeSitzung(true);
  /* Das Flag wandert in die App hinein, statt hier einen Zeitgeber zu
     starten. Der erste Anlauf tat genau das – und verlor gegen die
     Onboarding-Tour, die 100 ms später mit z-index 4000 über das Fenster
     (z-index 90) fiel. Was nacheinander passieren soll, gehört an eine
     Stelle, die beide Schritte kennt: enterApp(). */
  if (onSignedIn) await onSignedIn({ ...user, frischRegistriert: !!frischAngelegt });
  showVerifyBar(user);
}

/* ── Formular-Umschaltung ── */
const FORMS = {
  login: '#form-login', register: '#form-register',
  forgot: '#form-forgot', newpw: '#form-newpw', sent: '#form-sent',
};
let aktuellesFormular = 'login';
function showForm(which) {
  aktuellesFormular = which;
  for (const [key, sel] of Object.entries(FORMS)) $(sel)?.classList.toggle('hidden', key !== which);
  // Reiter und Schnellwege gehören zu Anmelden/Registrieren, nicht zu den
  // Reset-Schritten.
  $('.auth-tabs')?.classList.toggle('hidden', !istHauptformular());
  anbieterSichtbarkeit();
  $('#magic-sent')?.classList.add('hidden');
  msg('');
}
const istHauptformular = () => aktuellesFormular === 'login' || aktuellesFormular === 'register';

/* ── Passwortstärke ──────────────────────────────────────────────────────
   Nur eine Rückmeldung fürs Gefühl; die verbindliche Prüfung macht der Server.
   Bewusst an Länge und Zeichenvielfalt orientiert statt an Sonderzeichen-
   Pflicht – die erzeugt erfahrungsgemäß nur „Passwort1!". */
function pwScore(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  if (pw.length >= 20) s++;
  if (new Set(pw).size >= 10) s++;
  if (/^(.)\1+$/.test(pw)) s = 0;
  return Math.min(s, 4);
}
function wirePwMeter(inputId, meterId) {
  const input = document.getElementById(inputId);
  const meter = document.getElementById(meterId);
  if (!input || !meter) return;
  const bar = meter.querySelector('span');
  input.addEventListener('input', () => {
    const s = pwScore(input.value);
    meter.dataset.level = String(s);
    if (bar) bar.style.width = (s * 25) + '%';
    meter.setAttribute('aria-label', ['sehr schwach', 'schwach', 'geht so', 'gut', 'stark'][s]);
  });
}

/* ── Google ──────────────────────────────────────────────────────────────
   Die offizielle Bibliothek liefert ein signiertes ID-Token, das der Worker
   gegen Googles öffentliche Schlüssel prüft. Wir sehen kein Passwort. */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src; s.async = true; s.defer = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Skript nicht erreichbar: ' + src));
    document.head.appendChild(s);
  });
}

/* Der nonce bindet das ID-Token an genau diesen Anmeldeversuch: der Server
   gibt ihn aus, Google schreibt ihn in das Token, der Server löst ihn genau
   einmal ein. Ein abgefangenes Token ist danach wertlos – ohne das galt es bis
   zu einer Stunde lang für jeden, der es hatte.

   Er hält zehn Minuten. Wer das Anmeldefenster länger offen lässt, bekommt
   beim Klick eine neue Kennung untergeschoben und versucht es einfach erneut. */
async function initGoogle(clientId) {
  window.google.accounts.id.initialize({
    client_id: clientId,
    ux_mode: 'popup',
    nonce: await cloudNonce(),
    callback: (resp) => guard(null, async () => {
      try {
        const { user, neu } = await cloudOAuth('google', { credential: resp.credential }, rememberActive());
        await signedIn(user, 'Willkommen ✦', neu);
      } catch (err) {
        if (/Anmelde-Kennung/.test(err.message || '')) {
          await initGoogle(clientId);
          throw new Error('Der Anmeldeversuch war zu alt. Bitte einmal erneut auf den Google-Knopf tippen.');
        }
        throw err;
      }
    }),
  });
}

async function setupGoogle(clientId, host) {
  await loadScript('https://accounts.google.com/gsi/client');
  const g = window.google;
  if (!g || !g.accounts || !g.accounts.id) throw new Error('Google-Bibliothek nicht verfügbar');

  await initGoogle(clientId);

  /* Googles Gestaltungsvorgaben verlangen ihren eigenen Knopf. Bis er da ist,
     steht im Host ein Skeleton in seiner Größe – erst jetzt wird es ersetzt.

     Früher stand dort ein nachgebauter Knopf, der nach dem Laden gegen den
     echten getauscht wurde: zwei verschiedene Knöpfe kurz hintereinander an
     derselben Stelle. Ein Platzhalter, der sich nicht als fertiger Inhalt
     ausgibt, ist ehrlicher – und der Tausch fällt nicht mehr auf.

     Der Host muss dabei sichtbar sein: Google zeichnet in ein Element mit
     display:none mit Breite 0, man sieht dann schlicht nichts. */
  host.innerHTML = '';
  g.accounts.id.renderButton(host, {
    type: 'standard', theme: 'outline', size: 'large',
    text: 'continue_with', shape: 'pill', locale: 'de', width: 320,
  });
}

/* ── Apple ── */
async function setupApple(clientId, redirectURI, btn) {
  await loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/de_DE/appleid.auth.js');
  const A = window.AppleID;
  if (!A || !A.auth) throw new Error('Apple-Bibliothek nicht verfügbar');

  if (btn) {
    btn.onclick = () => guard(btn, async () => {
      // Bei Apple läuft die Anmeldung über den Klick – deshalb holen wir die
      // Einmal-Kennung genau hier, direkt davor. Sie kann gar nicht veralten.
      A.auth.init({ clientId, scope: 'name email', redirectURI, usePopup: true, nonce: await cloudNonce() });
      const res = await A.auth.signIn();
      const idToken = res && res.authorization && res.authorization.id_token;
      if (!idToken) throw new Error('Apple hat kein Token geliefert.');
      // Den Namen liefert Apple nur bei der allerersten Anmeldung mit.
      const name = res.user && res.user.name
        ? [res.user.name.firstName, res.user.name.lastName].filter(Boolean).join(' ')
        : '';
      const { user, neu } = await cloudOAuth('apple', { id_token: idToken, name }, rememberActive());
      await signedIn(user, 'Willkommen ✦', neu);
    });
  }
}

/* ── Anbieter-Bereich ────────────────────────────────────────────────────
   Was hier steht, entscheidet ausschließlich der Server über /auth/config.

   ── Der Fehler, der das nötig machte ─────────────────────────────────────
   Google- und Apple-Knopf standen fest im HTML. Sobald die Config sagte
   „Google ja", wurde der ganze Kasten sichtbar – mit BEIDEN Knöpfen. Erst
   nachdem Googles Bibliothek über das Netz geladen war (mehrere hundert
   Millisekunden), verschwand Apple wieder.

   Sichtbar war also ein Anmeldeweg, den es bei uns gar nicht gibt. Das per
   CSS zu verstecken hätte das Symptom verschoben, nicht behoben: der Knopf
   wäre weiter im Dokument, für Screenreader und Tastatur erreichbar, und
   beim nächsten Umbau wieder sichtbar. Deshalb wird er jetzt gar nicht erst
   erzeugt.

   Der Apple-Zweig bleibt vollständig funktionsfähig – der Server ist dafür
   vorbereitet, es fehlt nur die APPLE_CLIENT_ID. Sobald sie gesetzt ist,
   erscheint der Knopf ohne weitere Änderung hier. */
let anbieterDa = false;      // hat mindestens ein Anbieter es bis zum Knopf geschafft?
let anbieterLaeuft = false;  // schon aufgerufen? (Aufruf ist mehrfach erlaubt)

function anbieterSichtbarkeit() {
  const box = $('#auth-social');
  const zeigen = anbieterDa && istHauptformular();
  box?.classList.toggle('hidden', !zeigen);
  box?.classList.toggle('leer', !anbieterDa);
  $('#auth-or')?.classList.toggle('hidden', !zeigen);
}

const APPLE_ICON = '<svg class="social-ico" viewBox="0 0 18 18" aria-hidden="true">'
  + '<path fill="currentColor" d="M12.4 9.6c0-1.9 1.55-2.8 1.62-2.85-.88-1.3-2.26-1.47-2.75-1.49-1.17-.12-2.29.69-2.88.69-.6 0-1.51-.67-2.48-.66-1.28.02-2.46.74-3.12 1.88-1.33 2.31-.34 5.73.96 7.6.64.92 1.4 1.95 2.4 1.91.96-.04 1.33-.62 2.49-.62s1.49.62 2.5.6c1.04-.02 1.69-.93 2.32-1.86.73-1.06 1.03-2.09 1.05-2.15-.02-.01-2.02-.78-2.04-3.09zM10.5 3.9c.53-.64.89-1.53.79-2.42-.76.03-1.69.51-2.24 1.15-.49.56-.92 1.47-.81 2.34.85.06 1.72-.43 2.26-1.07z"/></svg>';

export async function zeigeAnbieter() {
  if (anbieterLaeuft) { anbieterSichtbarkeit(); return; }
  anbieterLaeuft = true;

  const box = $('#auth-social');
  if (!box) return;

  let cfg = {};
  try { cfg = await cloudAuthConfig(); } catch { /* ohne Config: nur E-Mail */ }
  const google = !!(cfg.google && cfg.googleClientId);
  const apple = !!(cfg.apple && cfg.appleClientId);
  if (!google && !apple) { anbieterDa = false; anbieterSichtbarkeit(); return; }

  /* Ab hier steht fest, dass mindestens ein Knopf kommt. Der Kasten wird
     sichtbar und hält mit einem Skeleton den Platz frei – sonst rutscht das
     Formular darunter nach unten, sobald der Knopf eintrifft. */
  box.innerHTML = '';
  anbieterDa = true;
  anbieterSichtbarkeit();

  let host = null;
  if (google) {
    host = document.createElement('div');
    host.className = 'google-host';
    host.id = 'google-host';
    host.innerHTML = '<span class="skel skel-block" style="width:320px;max-width:100%;height:40px;border-radius:999px"></span>';
    box.appendChild(host);
  }
  let appleBtn = null;
  if (apple) {
    appleBtn = document.createElement('button');
    appleBtn.type = 'button';
    appleBtn.className = 'social-btn';
    appleBtn.id = 'btn-apple';
    appleBtn.innerHTML = APPLE_ICON + '<span>Weiter mit Apple</span>';
    box.appendChild(appleBtn);
  }

  /* Beide Anbieter parallel einrichten: Apple soll nicht warten, bis Googles
     Skript da ist. Scheitert einer, verschwindet nur sein Knopf. */
  await Promise.all([
    google ? setupGoogle(cfg.googleClientId, host).catch((err) => {
      console.warn('Google-Anmeldung nicht verfügbar:', err.message);
      host.remove();
    }) : null,
    apple ? setupApple(cfg.appleClientId, cfg.appUrl || location.origin, appleBtn).catch((err) => {
      console.warn('Apple-Anmeldung nicht verfügbar:', err.message);
      appleBtn.remove();
    }) : null,
  ].filter(Boolean));

  // Wenn beide gescheitert sind, bleibt nur der E-Mail-Weg – dann auch ohne
  // „oder mit E-Mail"-Trenner, der sonst nichts mehr trennt.
  anbieterDa = box.children.length > 0;
  anbieterSichtbarkeit();
}

/* „Angemeldet bleiben" gibt es nur noch einmal, im Anmeldeformular. Das
   Registrierungsformular braucht es nicht mehr: dort entsteht keine Sitzung,
   die man behalten könnte – die kommt erst mit dem Bestätigungslink.
   (Vorher gab es zwei Checkboxen, und nach dem Abmelden wurde regelmäßig die
   falsche gelesen.) */
const rememberFrom = (sel) => { const b = $(sel); return b ? b.checked : true; };
const rememberActive = () => rememberFrom('#li-remember');

/* ── Hinweisleiste „E-Mail bestätigen" ── */
export function showVerifyBar(user) {
  const bar = $('#verify-bar');
  if (!bar) return;
  const noetig = user && user.emailVerified === false;
  bar.classList.toggle('hidden', !noetig);
}

/* ── Links aus E-Mails (?verify= / ?reset= / ?newmail=) ──────────────────
   Gibt true zurück, wenn die App dadurch schon betreten wurde. */
export async function handleAuthLink(kind, token) {
  if (kind === 'reset') {
    pendingResetToken = token;
    if (wired) showForm('newpw');
    return false;
  }

  if (kind === 'newmail') {
    // Adresswechsel geht nur im angemeldeten Zustand – der Link allein soll
    // niemandem die Kontrolle über ein fremdes Konto geben.
    if (!hasSession()) {
      showForm('login');
      msg('Melde dich zuerst an, dann bestätigen wir deine neue Adresse.', 'err');
      return false;
    }
    try {
      const d = await cloudConfirmEmailChange(token);
      msg('Neue Adresse bestätigt ✦ ' + (d.email || ''), 'ok');
    } catch (err) {
      msg(err.message || 'Der Link hat nicht funktioniert.', 'err');
    }
    return false;
  }

  /* E-Mail bestätigen. Das ist zugleich der erste Zugang zum Konto: der
     Server stellt hier die Sitzung aus, weil der Empfang der Mail den
     Zugriff auf das Postfach beweist. */
  try {
    const user = await cloudVerifyEmail(token);
    await signedIn(user, 'E-Mail bestätigt ✦ Willkommen bei mimorio');
    return true;
  } catch (err) {
    showForm('login');
    msg(err.message || 'Der Bestätigungslink hat nicht funktioniert.', 'err');
  }
  return false;
}

/* Google beim Abmelden Bescheid geben.

   Google verlangt diesen Aufruf ausdrücklich für den Abmeldefall: er
   verhindert, dass die Bibliothek beim nächsten Besuch automatisch wieder
   anmeldet („UX dead loop").

   Was er ausdrücklich NICHT tut – hier stand zuerst das Gegenteil, deshalb
   die Klarstellung: Der personalisierte Knopf („Als Joey fortfahren") bleibt.
   Der hängt an der Google-Sitzung des Browsers und daran, dass dieses Konto
   der App schon einmal zugestimmt hat. Laut Googles Referenz gibt es keine
   Einstellung, um einen generischen Knopf zu erzwingen; zum Wechseln dient
   der kleine Pfeil im Knopf.

   Wirklich zurücksetzen lässt sich das nur, indem die Zustimmung
   zurückgezogen wird (`revoke`) – das passiert bei uns beim Trennen der
   Verknüpfung in den Einstellungen, wo es auch inhaltlich hingehört. Beim
   bloßen Abmelden wäre es zu viel: danach müsste man der App jedes Mal neu
   zustimmen. */
export function forgetGoogleChoice() {
  try { window.google?.accounts?.id?.disableAutoSelect(); }
  catch (err) { console.warn('Google-Auswahl konnte nicht zurückgesetzt werden:', err); }
}

/* Nach dem Abmelden wieder ein definierter Anfangszustand. */
export function resetAuthScreen() {
  forgetGoogleChoice();
  pendingResetToken = '';
  $$('.auth-tab').forEach((x, i) => {
    x.classList.toggle('active', i === 0);
    x.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
  });
  $$('.pw-eye').forEach((e) => e.classList.remove('on'));
  ['#li-pass', '#re-pass', '#np-pass'].forEach((s) => { const el = $(s); if (el) el.type = 'password'; });
  $('#verify-bar')?.classList.add('hidden');
  showForm('login');
}

/* ── Verdrahtung ── */
export async function wireCloudAuth(enterApp) {
  onSignedIn = enterApp;

  /* Der Anbieter-Bereich wird hier NICHT geladen. Er kommt erst, wenn
     feststeht, dass jemand ihn braucht – also im Gast-Zustand (main.js) oder
     nach dem Abmelden. Wer angemeldet ist, lädt so weder Googles Skript noch
     die Anbieter-Abfrage: eine Netzanfrage und ein Fremdskript weniger auf
     dem Weg zur eigentlichen App. */

  $('#auth-note').textContent = 'Dein Lernstand wird verschlüsselt gespeichert und geräteübergreifend synchronisiert.';

  // Passwort ein-/ausblenden
  $$('.pw-eye').forEach((eye) => {
    eye.onclick = () => {
      const input = document.getElementById(eye.dataset.pw);
      if (!input) return;
      const show = input.type === 'password';
      input.type = show ? 'text' : 'password';
      eye.classList.toggle('on', show);
      eye.setAttribute('aria-label', show ? 'Passwort verbergen' : 'Passwort anzeigen');
    };
  });
  wirePwMeter('re-pass', 're-meter');
  wirePwMeter('np-pass', 'np-meter');

  // Reiter
  $$('.auth-tab').forEach((tab) => {
    tab.onclick = () => {
      $$('.auth-tab').forEach((x) => { x.classList.remove('active'); x.setAttribute('aria-selected', 'false'); });
      tab.classList.add('active'); tab.setAttribute('aria-selected', 'true');
      showForm(tab.dataset.auth === 'register' ? 'register' : 'login');
    };
  });

  // Anmelden
  $('#form-login').onsubmit = (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type=submit]');
    guard(btn, async () => {
      const user = await cloudLogin($('#li-id').value.trim(), $('#li-pass').value, rememberFrom('#li-remember'));
      await signedIn(user, 'Willkommen zurück ✦');
    });
  };

  /* Registrieren meldet bewusst NICHT an.

     Der Zugang entsteht erst mit dem Klick auf den Bestätigungslink. Damit
     lässt sich ein Konto auf eine fremde Adresse gar nicht erst benutzen –
     und die Antwort ist für jede Adresse dieselbe, egal ob es sie schon gibt.
     Wer hier schon ein Konto hat, erfährt das per Mail an genau diese
     Adresse, nicht über das Formular. */
  $('#form-register').onsubmit = (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type=submit]');
    guard(btn, async () => {
      const mail = $('#re-email').value.trim();
      await cloudRegister(mail, $('#re-pass').value, $('#re-user').value.trim());
      showForm('sent');
      $('#sent-text').textContent = `Wir haben eine E-Mail an ${mail} geschickt. `
        + 'Mit dem Link darin bestätigst du deine Adresse und bist sofort drin. Schau auch im Spam nach.';
    });
  };
  $('#btn-sent-back').onclick = () => showForm('login');

  // Passwort vergessen
  $('#btn-forgot').onclick = () => {
    showForm('forgot');
    const mail = $('#li-id').value.trim();
    if (mail) $('#fg-email').value = mail;
  };
  $('#btn-forgot-back').onclick = () => showForm('login');
  $('#form-forgot').onsubmit = (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type=submit]');
    guard(btn, async () => {
      await cloudForgot($('#fg-email').value.trim());
      const box = $('#magic-sent');
      box.classList.remove('hidden');
      // Bewusst neutral formuliert: ob es das Konto gibt, verraten wir nicht.
      box.textContent = 'Falls es zu dieser Adresse ein Konto gibt, ist der Link unterwegs. Schau auch im Spam nach.';
    });
  };

  // Neues Passwort nach Klick auf den Reset-Link
  $('#form-newpw').onsubmit = (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type=submit]');
    guard(btn, async () => {
      if (!pendingResetToken) throw new Error('Der Link fehlt. Fordere einen neuen an.');
      const user = await cloudResetPassword(pendingResetToken, $('#np-pass').value, true);
      pendingResetToken = '';
      await signedIn(user, 'Neues Passwort gespeichert ✦');
    });
  };

  // Hinweisleiste
  const vb = $('#verify-resend');
  if (vb) vb.onclick = () => guard(vb, async () => {
    await cloudResendVerification();
    $('#verify-text').textContent = 'Neuer Bestätigungslink ist unterwegs – schau in dein Postfach.';
    vb.classList.add('hidden');
  });
  const vc = $('#verify-close');
  if (vc) vc.onclick = () => $('#verify-bar').classList.add('hidden');

  wired = true;
  showForm(pendingResetToken ? 'newpw' : 'login');
}
